import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Film,
  Sparkles,
  RotateCcw
} from 'lucide-react';
import { FRANKIE_VIDEOS, FrankieVideoItem } from '../data/restaurantData';

const getAssetUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const base = import.meta.env.BASE_URL || '/';
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBase}${cleanUrl}`;
};

// Custom titles and descriptions tailored to the featured motion showcase
const VIDEO_SHOWCASE_METADATA = [
  {
    number: 1,
    tag: "A DAY AT FRANKIE'S",
    title: 'RAMSGATE BEACH ATMOSPHERE & GRILL',
    description: 'Experience the real atmosphere right on Ramsgate Sands with fresh food cooking on the grill and sea breezes.',
    src: '/video-1-frankie.mp4',
    poster: '/video-1-poster.jpg',
  },
  {
    number: 2,
    tag: "A DAY AT FRANKIE'S",
    title: 'SEASIDE BOARDWALK ENERGY',
    description: 'A genuine glimpse of Frankie’s kiosk in action on the sands — laughter, seaside energy, and coastal memories.',
    src: '/video-2-frankie.mp4',
    poster: '/video-2-poster.jpg',
  },
  {
    number: 3,
    tag: "A DAY AT FRANKIE'S",
    title: 'AWARD-WINNING REFRESHMENTS & BAR',
    description: 'TripAdvisor Travelers’ Choice award-winning food, refreshing iced slushies, beach cocktails, and ice-cold beers served right on the beach.',
    src: '/video-3-frankie.mp4',
    poster: '/video-3-poster.jpg',
  },
  {
    number: 4,
    tag: "A DAY AT FRANKIE'S",
    title: 'BEACHFRONT FUN FOR EVERYONE',
    description: "See the lively beachfront and make a day of it at Frankie's.",
    src: '/video-4-frankie.mp4',
    poster: '/video-4-poster.jpg',
  },
  {
    number: 5,
    tag: "A DAY AT FRANKIE'S",
    title: "FOSTER'S PARASOLS & BEACH DECKING",
    description: 'Sunlit panoramic view of Frankie’s outdoor picnic tables with Foster’s umbrellas, seaside crowd, and the open Kent sea.',
    src: '/video-5-frankie.mp4',
    poster: '/video-5-poster.jpg',
  },
  {
    number: 6,
    tag: "A DAY AT FRANKIE'S",
    title: "CHILDREN'S CAROUSEL ON THE SANDS",
    description: 'Mickey and Minnie Mouse carousel spinning on the sands beside Frankie’s kiosk, seaside promenade, and sunny beach vibes.',
    src: '/video-6-frankie.mp4',
    poster: '/video-6-poster.jpg',
  },
];

const ROTATION_INTERVAL_MS = 8000; // 8 seconds auto-rotation as requested

export const FrankiesVideoSection: React.FC = () => {
  const totalVideos = VIDEO_SHOWCASE_METADATA.length;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isAutoRotating, setIsAutoRotating] = useState<boolean>(true);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const currentVideo = VIDEO_SHOWCASE_METADATA[currentIndex];

  // Auto rotation timer effect
  useEffect(() => {
    if (!isAutoRotating) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalVideos);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isAutoRotating, totalVideos, currentIndex]);

  // When active video index changes, safely update video source and autoplay
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.src = getAssetUrl(currentVideo.src);
    video.currentTime = 0;
    video.muted = isMuted;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => setIsPlaying(true))
        .catch(() => {
          // Autoplay fallback with mute
          video.muted = true;
          setIsMuted(true);
          video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        });
    }
  }, [currentIndex, currentVideo.src, isMuted]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalVideos - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalVideos);
  };

  const handleSelectVideo = (index: number) => {
    setCurrentIndex(index);
  };

  const toggleAutoRotate = () => {
    setIsAutoRotating((prev) => !prev);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextMuted = !isMuted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const handleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    if (!duration && videoRef.current.duration) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration || 0);
  };

  const formatTime = (secs: number) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <section
      id="frankies-beach-videos-section"
      aria-label="Frankie's Beach Moments"
      className="py-14 sm:py-18 bg-gradient-to-b from-[#0A2647] via-[#0D345F] to-[#0A223E] text-white relative overflow-hidden border-t border-b border-sky-900/50"
    >
      {/* Decorative Ambient Lighting */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#0084FF]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#ECD87A]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header: Left Title / Right Subtext */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 sm:mb-10">
          <div>
            <span className="text-[#ECD87A] text-xs font-black uppercase tracking-widest font-heading block mb-2">
              FRANKIE'S BEACH MOMENTS
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase font-heading text-white tracking-tight leading-none">
              A WHOLE DAY AT THE BEACH,<br />
              IN MOTION.
            </h2>
          </div>

          <p className="text-sky-200 text-sm sm:text-base max-w-md leading-relaxed font-normal">
            Real moments from the food, funfair and beachfront atmosphere that keep people coming back.
          </p>
        </div>

        {/* Featured Video Showcase Card */}
        <div className="bg-[#0c2f5d]/75 border border-sky-400/25 rounded-3xl p-4 sm:p-7 shadow-2xl backdrop-blur-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            
            {/* Left Column: Video Player Container */}
            <div className="lg:col-span-7">
              <div
                ref={containerRef}
                className="relative rounded-2xl overflow-hidden bg-black/60 aspect-video shadow-2xl border border-white/20 group flex flex-col justify-end"
              >
                {/* Top-Left Badge: FRANKIE'S VIDEO • 0X / 06 */}
                <div className="absolute top-4 left-4 z-20">
                  <div className="border border-[#ECD87A] bg-black/60 backdrop-blur-md text-[#ECD87A] font-heading font-black text-[11px] sm:text-xs uppercase px-3 py-1 rounded-md tracking-wider shadow-lg flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ECD87A] animate-pulse" />
                    <span>
                      FRANKIE'S VIDEO • {String(currentVideo.number).padStart(2, '0')} / {String(totalVideos).padStart(2, '0')}
                    </span>
                  </div>
                </div>

                {/* HTML5 Video Element */}
                <video
                  ref={videoRef}
                  src={getAssetUrl(currentVideo.src)}
                  poster={getAssetUrl(currentVideo.poster)}
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onClick={togglePlay}
                  className="w-full h-full object-cover cursor-pointer"
                />

                {/* Bottom Control Bar on Hover / Active */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-20 flex items-center justify-between gap-3 text-white text-xs">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={togglePlay}
                      className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      title={isPlaying ? 'Pause video' : 'Play video'}
                      aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    >
                      {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-current" />}
                    </button>

                    <button
                      type="button"
                      onClick={toggleMute}
                      className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      title={isMuted ? 'Unmute audio' : 'Mute audio'}
                      aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                    >
                      {isMuted ? <VolumeX className="w-3.5 h-3.5 text-amber-300" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>

                    <span className="text-[11px] font-mono text-white/80">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleFullscreen}
                      className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors cursor-pointer"
                      title="Fullscreen"
                      aria-label="Fullscreen"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Title, Description, Controls */}
            <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
              <div className="space-y-3">
                <span className="text-sky-300 text-xs font-black uppercase tracking-widest font-heading block">
                  {currentVideo.tag}
                </span>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase text-white font-heading leading-tight tracking-tight">
                  {currentVideo.title}
                </h3>

                <p className="text-sm sm:text-base text-sky-100/90 leading-relaxed font-normal">
                  {currentVideo.description}
                </p>
              </div>

              {/* Navigation Arrows & Pagination Dots */}
              <div className="pt-2">
                <div className="flex items-center gap-3">
                  {/* Previous Button */}
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="w-10 h-10 rounded-full border border-sky-400/50 hover:border-sky-300 bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center transition-all cursor-pointer shadow-sm shrink-0"
                    title="Previous video"
                    aria-label="Previous video"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {/* Dots Pagination */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {VIDEO_SHOWCASE_METADATA.map((vid, idx) => {
                      const isActive = idx === currentIndex;
                      return (
                        <button
                          key={vid.number}
                          type="button"
                          onClick={() => handleSelectVideo(idx)}
                          className={`transition-all duration-300 cursor-pointer ${
                            isActive
                              ? 'w-7 h-2.5 bg-[#ECD87A] rounded-full shadow-sm'
                              : 'w-2.5 h-2.5 bg-white/30 hover:bg-white/60 rounded-full'
                          }`}
                          title={`Go to video ${vid.number}: ${vid.title}`}
                          aria-label={`Go to video ${vid.number}: ${vid.title}`}
                        />
                      );
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-10 h-10 rounded-full border border-sky-400/50 hover:border-sky-300 bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center transition-all cursor-pointer shadow-sm shrink-0"
                    title="Next video"
                    aria-label="Next video"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Bottom Divider Line with Rotation Status */}
              <div className="pt-4 border-t border-white/15 flex items-center justify-between text-xs text-sky-200">
                <button
                  type="button"
                  onClick={toggleAutoRotate}
                  className="flex items-center gap-1.5 hover:text-white font-medium transition-colors cursor-pointer"
                  title={isAutoRotating ? 'Pause automatic video rotation' : 'Resume automatic video rotation'}
                >
                  {isAutoRotating ? (
                    <>
                      <Pause className="w-3.5 h-3.5 fill-current text-[#ECD87A]" />
                      <span>Pause rotation (every 8s)</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-3.5 h-3.5 fill-current text-[#ECD87A]" />
                      <span>Resume rotation</span>
                    </>
                  )}
                </button>

                <span className="text-[11px] text-sky-300/80 font-medium">
                  Real Frankie's videos
                </span>
              </div>

            </div>

          </div>

          {/* Quick Video Selector Strip (All 6 Videos) */}
          <div className="mt-6 pt-5 border-t border-white/15 grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
            {VIDEO_SHOWCASE_METADATA.map((video, index) => {
              const isActive = index === currentIndex;
              return (
                <button
                  key={video.number}
                  type="button"
                  onClick={() => handleSelectVideo(index)}
                  className={`group relative rounded-xl overflow-hidden aspect-[16/10] border-2 transition-all cursor-pointer text-left ${
                    isActive
                      ? 'border-[#ECD87A] ring-2 ring-[#ECD87A]/40 scale-[1.02] shadow-lg'
                      : 'border-white/15 hover:border-white/40 opacity-70 hover:opacity-100'
                  }`}
                  title={`Play Video ${video.number}: ${video.title}`}
                >
                  <img
                    src={getAssetUrl(video.poster)}
                    alt={`Video ${video.number}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (!target.src.endsWith('.webp')) {
                        target.src = getAssetUrl(`/video-${video.number}-poster.webp`);
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <span className={`absolute bottom-1.5 left-1.5 text-[10px] font-heading font-black px-1.5 py-0.5 rounded ${
                    isActive ? 'bg-[#ECD87A] text-black' : 'bg-black/60 text-white'
                  }`}>
                    0{video.number}
                  </span>
                </button>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
};
